import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import {
  ChevronDown,
  ChevronRight,
  FileImage,
  Folder,
  FolderOpen,
  FolderPlus,
  FolderSearch,
  Grid2X2,
  ImagePlus,
  List,
  MoreHorizontal,
  Move,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  Search,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { formatStoredPath } from "../domain/paths";
import type { WorkspaceGateway } from "../workspace/workspace_gateway";
import {
  describeWorkspaceFolder,
  planWorkspaceMove,
  type WorkspaceMoveItem,
} from "../workspace/workspace_move";
import type {
  ScannedBoard,
  ScannedFolder,
  WorkspaceNode,
  WorkspaceScan,
} from "../workspace/workspace_scan";
import { BoardPreview } from "./board_preview";
import { IconButton } from "./icon_button";

export interface WorkspaceNavigatorProps {
  readonly activePath: readonly string[] | null;
  readonly collapsed: boolean;
  readonly gateway: WorkspaceGateway;
  readonly pinnedPaths: readonly string[];
  readonly recentPaths: readonly string[];
  readonly scan: WorkspaceScan;
  readonly trashCount: number;
  readonly workspaceName: string;
  readonly onCreateFolder: () => void;
  readonly onManageBoard: (board: ScannedBoard) => void;
  readonly onManageFolder: (folder: ScannedFolder) => void;
  readonly onMoveItem: (
    item: WorkspaceMoveItem,
    destinationFolderPath: readonly string[],
  ) => Promise<void>;
  readonly onNewBoard: () => void;
  readonly onOpenBoard: (board: ScannedBoard) => void;
  readonly onRenameBoard: (board: ScannedBoard) => void;
  readonly onRefresh: () => void;
  readonly onShowTrash: () => void;
  readonly onSwitchWorkspace: () => void;
  readonly onToggleCollapsed: () => void;
  readonly onTogglePin: (board: ScannedBoard) => void;
}

/** Renders the searchable filesystem-derived tree, recent boards, pins, grid, and trash. */
export function WorkspaceNavigator({
  activePath,
  collapsed,
  gateway,
  pinnedPaths,
  recentPaths,
  scan,
  trashCount,
  workspaceName,
  onCreateFolder,
  onManageBoard,
  onManageFolder,
  onMoveItem,
  onNewBoard,
  onOpenBoard,
  onRenameBoard,
  onRefresh,
  onShowTrash,
  onSwitchWorkspace,
  onToggleCollapsed,
  onTogglePin,
}: WorkspaceNavigatorProps) {
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"list" | "grid">("list");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const draggedItemRef = useRef<WorkspaceMoveItem | null>(null);
  const [draggedItem, setDraggedItem] = useState<WorkspaceMoveItem | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    readonly key: string;
    readonly valid: boolean;
  } | null>(null);
  const [movePending, setMovePending] = useState(false);
  const [moveFeedback, setMoveFeedback] = useState<{
    readonly kind: "status" | "error";
    readonly message: string;
  } | null>(null);
  const [expandedPaths, setExpandedPaths] = useState<ReadonlySet<string>>(
    () => new Set(scan.folders.map((folder) => formatStoredPath(folder.path))),
  );
  const pinned = useMemo(() => new Set(pinnedPaths), [pinnedPaths]);
  const boardsByPath = useMemo(
    () => new Map(scan.boards.map((board) => [formatStoredPath(board.path), board])),
    [scan.boards],
  );
  const recentBoards = recentPaths
    .map((path) => boardsByPath.get(path))
    .filter((board): board is ScannedBoard => board !== undefined);
  const pinnedBoards = pinnedPaths
    .map((path) => boardsByPath.get(path))
    .filter((board): board is ScannedBoard => board !== undefined);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const matchingBoards = scan.boards.filter((board) =>
    board.name.toLocaleLowerCase().includes(normalizedQuery),
  );
  const matchingFolders = scan.folders.filter((folder) =>
    folder.name.toLocaleLowerCase().includes(normalizedQuery),
  );

  useEffect(() => {
    /** Opens the navigator and focuses filesystem search from the documented shortcut. */
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLocaleLowerCase() !== "k") {
        return;
      }
      event.preventDefault();
      if (collapsed) {
        onToggleCollapsed();
        window.setTimeout(() => searchInputRef.current?.focus(), 0);
      } else {
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [collapsed, onToggleCollapsed]);

  /** Toggles one folder without discarding other navigation expansion state. */
  const toggleFolder = (folder: ScannedFolder): void => {
    const key = formatStoredPath(folder.path);
    setExpandedPaths((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  /** Reveals one search-matched folder and all its ancestors in the normal tree. */
  const revealFolder = (folder: ScannedFolder): void => {
    setExpandedPaths((current) => {
      const next = new Set(current);
      for (let length = 1; length <= folder.path.length; length += 1) {
        next.add(formatStoredPath(folder.path.slice(0, length)));
      }
      return next;
    });
    setQuery("");
  };

  /** Starts one internal filesystem move without exposing path text as an external drag payload. */
  const startDrag = (event: DragEvent<HTMLElement>, item: WorkspaceMoveItem): void => {
    if (movePending) {
      event.preventDefault();
      return;
    }
    draggedItemRef.current = item;
    setDraggedItem(item);
    setMoveFeedback({
      kind: "status",
      message: `Dragging ${item.kind} ${item.name}. Drop it on a folder or Workspace.`,
    });
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", item.name);
  };

  /** Clears transient drag styling after cancellation or completion. */
  const endDrag = (): void => {
    draggedItemRef.current = null;
    setDraggedItem(null);
    setDropTarget(null);
  };

  /** Marks only safe folder targets while retaining an explicit invalid-drop cursor. */
  const dragOverFolder = (
    event: DragEvent<HTMLElement>,
    destinationFolderPath: readonly string[],
  ): void => {
    const item = draggedItemRef.current;
    if (item === null) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const key = formatStoredPath(destinationFolderPath);
    try {
      planWorkspaceMove(item, destinationFolderPath);
      event.dataTransfer.dropEffect = "move";
      setDropTarget((current) =>
        current?.key === key && current.valid ? current : { key, valid: true },
      );
    } catch (caught) {
      event.dataTransfer.dropEffect = "none";
      setDropTarget((current) =>
        current?.key === key && !current.valid ? current : { key, valid: false },
      );
      const message =
        caught instanceof Error ? caught.message : "That workspace move is not available.";
      setMoveFeedback((current) =>
        current?.kind === "error" && current.message === message
          ? current
          : { kind: "error", message },
      );
    }
  };

  /** Removes target styling only after the pointer actually leaves that drop surface. */
  const leaveFolderTarget = (event: DragEvent<HTMLElement>, key: string): void => {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    setDropTarget((current) => (current?.key === key ? null : current));
  };

  /** Runs one validated journaled move and reports an actionable result. */
  const dropOnFolder = async (
    event: DragEvent<HTMLElement>,
    destinationFolderPath: readonly string[],
  ): Promise<void> => {
    event.preventDefault();
    event.stopPropagation();
    const item = draggedItemRef.current;
    if (item === null) {
      return;
    }
    setMovePending(true);
    try {
      planWorkspaceMove(item, destinationFolderPath);
      await onMoveItem(item, destinationFolderPath);
      setMoveFeedback({
        kind: "status",
        message: `Moved ${item.name} to ${describeWorkspaceFolder(destinationFolderPath)}.`,
      });
    } catch (caught) {
      setMoveFeedback({
        kind: "error",
        message:
          caught instanceof Error ? caught.message : "The workspace entry could not be moved.",
      });
    } finally {
      setMovePending(false);
      endDrag();
    }
  };

  /** Opens the public system directory picker at one verified folder handle. */
  const showDirectory = async (path: readonly string[]): Promise<void> => {
    try {
      await gateway.showDirectoryInPicker(path);
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") {
        return;
      }
      setMoveFeedback({
        kind: "error",
        message:
          caught instanceof Error
            ? caught.message
            : "The folder could not be shown in the system picker.",
      });
    }
  };

  if (collapsed) {
    return (
      <aside className="navigator navigator--collapsed" aria-label="Workspace navigator">
        <span className="wordmark__mark" aria-hidden="true">
          S
        </span>
        <NavigatorIconButton label="Expand navigator" onClick={onToggleCollapsed}>
          <PanelLeftOpen aria-hidden="true" />
        </NavigatorIconButton>
        <NavigatorIconButton label="New Board" onClick={onNewBoard}>
          <ImagePlus aria-hidden="true" />
        </NavigatorIconButton>
        <NavigatorIconButton label={trashAccessibleName(trashCount)} onClick={onShowTrash}>
          <Trash2 aria-hidden="true" />
        </NavigatorIconButton>
      </aside>
    );
  }

  return (
    <aside className="navigator" aria-label="Workspace navigator">
      <div className="navigator__brand">
        <span className="wordmark__mark" aria-hidden="true">
          S
        </span>
        <span className="navigator__identity">
          <strong>Shotboard</strong>
          <small>{workspaceName}</small>
        </span>
        <NavigatorIconButton label="Collapse navigator" onClick={onToggleCollapsed}>
          <PanelLeftClose aria-hidden="true" />
        </NavigatorIconButton>
      </div>
      <button className="new-board" type="button" onClick={onNewBoard}>
        <ImagePlus aria-hidden="true" />
        New Board
      </button>

      <label className="navigator-search">
        <Search aria-hidden="true" />
        <span className="visually-hidden">Search workspace names</span>
        <input
          ref={searchInputRef}
          type="search"
          value={query}
          placeholder="Search boards and folders"
          onChange={(event) => setQuery(event.currentTarget.value)}
        />
        {query.length === 0 ? null : (
          <IconButton label="Clear search" onClick={() => setQuery("")}>
            <X aria-hidden="true" />
          </IconButton>
        )}
      </label>

      <fieldset
        className={`navigator__heading navigator__root-drop${dropTarget?.key === "" ? (dropTarget.valid ? " is-drop-target" : " is-drop-target-invalid") : ""}`}
        aria-label="Workspace root drop target"
        onDragOver={(event) => dragOverFolder(event, [])}
        onDragLeave={(event) => leaveFolderTarget(event, "")}
        onDrop={(event) => void dropOnFolder(event, [])}
      >
        <span>Workspace</span>
        <span className="navigator__heading-actions">
          <NavigatorIconButton
            label="Show Workspace in system picker"
            onClick={() => void showDirectory([])}
          >
            <FolderSearch aria-hidden="true" />
          </NavigatorIconButton>
          <NavigatorIconButton label="Create folder" onClick={onCreateFolder}>
            <FolderPlus aria-hidden="true" />
          </NavigatorIconButton>
          <NavigatorIconButton label="Refresh workspace" onClick={onRefresh}>
            <RefreshCw aria-hidden="true" />
          </NavigatorIconButton>
          <NavigatorIconButton
            label={view === "list" ? "Show thumbnail grid" : "Show folder list"}
            pressed={view === "grid"}
            onClick={() => setView((current) => (current === "list" ? "grid" : "list"))}
          >
            {view === "list" ? <Grid2X2 aria-hidden="true" /> : <List aria-hidden="true" />}
          </NavigatorIconButton>
        </span>
      </fieldset>

      <div className="navigator__content">
        {normalizedQuery.length > 0 ? (
          <SearchResults
            boards={matchingBoards}
            folders={matchingFolders}
            activePath={activePath}
            pinned={pinned}
            onOpenBoard={onOpenBoard}
            onManageBoard={onManageBoard}
            onDragEnd={endDrag}
            onDragStart={startDrag}
            onRenameBoard={onRenameBoard}
            onRevealFolder={revealFolder}
            onManageFolder={onManageFolder}
            onShowDirectory={showDirectory}
            onTogglePin={onTogglePin}
          />
        ) : view === "grid" ? (
          <BoardGrid
            boards={scan.boards}
            gateway={gateway}
            activePath={activePath}
            pinned={pinned}
            onOpenBoard={onOpenBoard}
            onManageBoard={onManageBoard}
            onDragEnd={endDrag}
            onDragStart={startDrag}
            onRenameBoard={onRenameBoard}
            onTogglePin={onTogglePin}
          />
        ) : (
          <>
            <BoardSection
              label="Pinned"
              boards={pinnedBoards}
              activePath={activePath}
              pinned={pinned}
              onOpenBoard={onOpenBoard}
              onManageBoard={onManageBoard}
              onDragEnd={endDrag}
              onDragStart={startDrag}
              onRenameBoard={onRenameBoard}
              onTogglePin={onTogglePin}
            />
            <BoardSection
              label="Recent"
              boards={recentBoards}
              activePath={activePath}
              pinned={pinned}
              onOpenBoard={onOpenBoard}
              onManageBoard={onManageBoard}
              onDragEnd={endDrag}
              onDragStart={startDrag}
              onRenameBoard={onRenameBoard}
              onTogglePin={onTogglePin}
            />
            {scan.children.length === 0 ? (
              <p className="navigator__empty">No saved boards or folders yet.</p>
            ) : (
              <ul className="workspace-tree" aria-label="Workspace tree">
                {scan.children.map((node) => (
                  <WorkspaceTreeNode
                    key={formatStoredPath(node.path)}
                    node={node}
                    activePath={activePath}
                    expandedPaths={expandedPaths}
                    pinned={pinned}
                    onManageBoard={onManageBoard}
                    draggedItem={draggedItem}
                    dropTarget={dropTarget}
                    onDragEnd={endDrag}
                    onDragOverFolder={dragOverFolder}
                    onDragLeaveFolder={leaveFolderTarget}
                    onDragStart={startDrag}
                    onDropFolder={dropOnFolder}
                    onManageFolder={onManageFolder}
                    onOpenBoard={onOpenBoard}
                    onRenameBoard={onRenameBoard}
                    onShowDirectory={showDirectory}
                    onToggleFolder={toggleFolder}
                    onTogglePin={onTogglePin}
                  />
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {moveFeedback === null ? null : (
        <p
          className={`navigator__move-feedback navigator__move-feedback--${moveFeedback.kind}`}
          role={moveFeedback.kind === "error" ? "alert" : "status"}
        >
          {moveFeedback.message}
        </p>
      )}

      <button
        className="navigator-footer-action"
        type="button"
        aria-label={trashAccessibleName(trashCount)}
        onClick={onShowTrash}
      >
        <Trash2 aria-hidden="true" />
        Trash
        <span>{trashCount}</span>
      </button>
      <button className="switch-workspace" type="button" onClick={onSwitchWorkspace}>
        <PanelLeftClose aria-hidden="true" />
        Switch Workspace
      </button>
    </aside>
  );
}

/** Formats one grammatical trash count for icon-only and visible controls. */
function trashAccessibleName(count: number): string {
  return `Trash, ${count} ${count === 1 ? "board" : "boards"}`;
}

interface BoardListProps {
  readonly activePath: readonly string[] | null;
  readonly boards: readonly ScannedBoard[];
  readonly pinned: ReadonlySet<string>;
  readonly onDragEnd: () => void;
  readonly onDragStart: (event: DragEvent<HTMLElement>, item: WorkspaceMoveItem) => void;
  readonly onManageBoard: (board: ScannedBoard) => void;
  readonly onOpenBoard: (board: ScannedBoard) => void;
  readonly onRenameBoard: (board: ScannedBoard) => void;
  readonly onTogglePin: (board: ScannedBoard) => void;
}

/** Renders one optional named board shortcut section. */
function BoardSection({ boards, ...rowProps }: BoardListProps & { readonly label: string }) {
  if (boards.length === 0) {
    return null;
  }
  return (
    <section className="board-section" aria-labelledby={`section-${rowProps.label.toLowerCase()}`}>
      <h2 id={`section-${rowProps.label.toLowerCase()}`}>{rowProps.label}</h2>
      <ul>
        {boards.map((board) => (
          <li key={formatStoredPath(board.path)}>
            <BoardRow board={board} {...rowProps} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Renders recursive folders and boards while preserving expansion across scans. */
function WorkspaceTreeNode({
  node,
  activePath,
  expandedPaths,
  pinned,
  draggedItem,
  dropTarget,
  onDragEnd,
  onDragLeaveFolder,
  onDragOverFolder,
  onDragStart,
  onDropFolder,
  onManageBoard,
  onManageFolder,
  onOpenBoard,
  onRenameBoard,
  onShowDirectory,
  onToggleFolder,
  onTogglePin,
}: {
  readonly node: WorkspaceNode;
  readonly activePath: readonly string[] | null;
  readonly expandedPaths: ReadonlySet<string>;
  readonly pinned: ReadonlySet<string>;
  readonly draggedItem: WorkspaceMoveItem | null;
  readonly dropTarget: { readonly key: string; readonly valid: boolean } | null;
  readonly onDragEnd: () => void;
  readonly onDragLeaveFolder: (event: DragEvent<HTMLElement>, key: string) => void;
  readonly onDragOverFolder: (
    event: DragEvent<HTMLElement>,
    destinationFolderPath: readonly string[],
  ) => void;
  readonly onDragStart: (event: DragEvent<HTMLElement>, item: WorkspaceMoveItem) => void;
  readonly onDropFolder: (
    event: DragEvent<HTMLElement>,
    destinationFolderPath: readonly string[],
  ) => Promise<void>;
  readonly onManageBoard: (board: ScannedBoard) => void;
  readonly onManageFolder: (folder: ScannedFolder) => void;
  readonly onOpenBoard: (board: ScannedBoard) => void;
  readonly onRenameBoard: (board: ScannedBoard) => void;
  readonly onShowDirectory: (path: readonly string[]) => Promise<void>;
  readonly onToggleFolder: (folder: ScannedFolder) => void;
  readonly onTogglePin: (board: ScannedBoard) => void;
}) {
  if (node.kind === "board") {
    return (
      <li>
        <BoardRow
          board={node}
          activePath={activePath}
          pinned={pinned}
          onDragEnd={onDragEnd}
          onDragStart={onDragStart}
          onManageBoard={onManageBoard}
          onOpenBoard={onOpenBoard}
          onRenameBoard={onRenameBoard}
          onTogglePin={onTogglePin}
        />
      </li>
    );
  }
  const key = formatStoredPath(node.path);
  const expanded = expandedPaths.has(key);
  const dragging =
    draggedItem !== null && formatStoredPath(draggedItem.path) === formatStoredPath(node.path);
  return (
    <li>
      <fieldset
        className={`folder-row-shell${dragging ? " is-dragging" : ""}${dropTarget?.key === key ? (dropTarget.valid ? " is-drop-target" : " is-drop-target-invalid") : ""}`}
        aria-label={`${node.name} folder drop target`}
        onDragOver={(event) => onDragOverFolder(event, node.path)}
        onDragLeave={(event) => onDragLeaveFolder(event, key)}
        onDrop={(event) => void onDropFolder(event, node.path)}
      >
        <button
          className="folder-row folder-row--tree"
          type="button"
          draggable
          aria-expanded={expanded}
          onClick={() => onToggleFolder(node)}
          onDragEnd={onDragEnd}
          onDragStart={(event) => onDragStart(event, node)}
        >
          {expanded ? (
            <ChevronDown className="folder-row__disclosure" aria-hidden="true" />
          ) : (
            <ChevronRight className="folder-row__disclosure" aria-hidden="true" />
          )}
          {expanded ? <FolderOpen aria-hidden="true" /> : <Folder aria-hidden="true" />}
          <span>{node.name}</span>
        </button>
        <NavigatorIconButton
          label={`Show ${node.name} in system picker`}
          onClick={() => void onShowDirectory(node.path)}
        >
          <FolderSearch aria-hidden="true" />
        </NavigatorIconButton>
        <NavigatorIconButton label={`Move ${node.name}`} onClick={() => onManageFolder(node)}>
          <Move aria-hidden="true" />
        </NavigatorIconButton>
      </fieldset>
      {expanded && node.children.length > 0 ? (
        <ul>
          {node.children.map((child) => (
            <WorkspaceTreeNode
              key={formatStoredPath(child.path)}
              node={child}
              activePath={activePath}
              expandedPaths={expandedPaths}
              pinned={pinned}
              draggedItem={draggedItem}
              dropTarget={dropTarget}
              onDragEnd={onDragEnd}
              onDragLeaveFolder={onDragLeaveFolder}
              onDragOverFolder={onDragOverFolder}
              onDragStart={onDragStart}
              onDropFolder={onDropFolder}
              onManageBoard={onManageBoard}
              onManageFolder={onManageFolder}
              onOpenBoard={onOpenBoard}
              onRenameBoard={onRenameBoard}
              onShowDirectory={onShowDirectory}
              onToggleFolder={onToggleFolder}
              onTogglePin={onTogglePin}
            />
          ))}
        </ul>
      ) : null}
      {expanded && node.children.length === 0 ? (
        <p className="folder-row__empty">This folder has no boards.</p>
      ) : null}
    </li>
  );
}

/** Renders one compact board with pin and management actions. */
function BoardRow({
  board,
  activePath,
  pinned,
  onDragEnd,
  onDragStart,
  onManageBoard,
  onOpenBoard,
  onRenameBoard,
  onTogglePin,
}: Omit<BoardListProps, "boards"> & { readonly board: ScannedBoard }) {
  const key = formatStoredPath(board.path);
  const current = activePath !== null && key === formatStoredPath(activePath);
  return (
    <div className="board-row">
      <button
        type="button"
        draggable
        aria-current={current ? "page" : undefined}
        aria-label={board.status === "valid" ? board.name : `${board.name}, ${board.status}`}
        onClick={(event) => {
          if (event.detail < 2) {
            onOpenBoard(board);
          }
        }}
        onDoubleClick={() => onRenameBoard(board)}
        onDragEnd={onDragEnd}
        onDragStart={(event) => onDragStart(event, board)}
        title="Double-click to rename"
      >
        <FileImage aria-hidden="true" />
        <span>{board.name}</span>
        {board.status === "valid" ? null : <small>{board.status}</small>}
      </button>
      <NavigatorIconButton
        label={`${pinned.has(key) ? "Unpin" : "Pin"} ${board.name}`}
        pressed={pinned.has(key)}
        onClick={() => onTogglePin(board)}
      >
        <Star aria-hidden="true" />
      </NavigatorIconButton>
      <NavigatorIconButton label={`Manage ${board.name}`} onClick={() => onManageBoard(board)}>
        <MoreHorizontal aria-hidden="true" />
      </NavigatorIconButton>
    </div>
  );
}

/** Renders flat matching folder and board names while a search is active. */
function SearchResults({
  boards,
  folders,
  onManageFolder,
  onRevealFolder,
  onShowDirectory,
  ...rowProps
}: BoardListProps & {
  readonly folders: readonly ScannedFolder[];
  readonly onManageFolder: (folder: ScannedFolder) => void;
  readonly onRevealFolder: (folder: ScannedFolder) => void;
  readonly onShowDirectory: (path: readonly string[]) => Promise<void>;
}) {
  if (boards.length === 0 && folders.length === 0) {
    return <p className="navigator__empty">No matching boards or folders.</p>;
  }
  return (
    <ul className="search-results" aria-label="Workspace search results">
      {folders.map((folder) => (
        <li key={formatStoredPath(folder.path)}>
          <div className="folder-row-shell folder-row-shell--search">
            <button
              className="folder-row"
              type="button"
              draggable
              onClick={() => onRevealFolder(folder)}
              onDragEnd={rowProps.onDragEnd}
              onDragStart={(event) => rowProps.onDragStart(event, folder)}
            >
              <Folder aria-hidden="true" />
              <span>{folder.name}</span>
            </button>
            <NavigatorIconButton
              label={`Show ${folder.name} in system picker`}
              onClick={() => void onShowDirectory(folder.path)}
            >
              <FolderSearch aria-hidden="true" />
            </NavigatorIconButton>
            <NavigatorIconButton
              label={`Move ${folder.name}`}
              onClick={() => onManageFolder(folder)}
            >
              <Move aria-hidden="true" />
            </NavigatorIconButton>
          </div>
        </li>
      ))}
      {boards.map((board) => (
        <li key={formatStoredPath(board.path)}>
          <BoardRow board={board} {...rowProps} />
        </li>
      ))}
    </ul>
  );
}

/** Renders every visible board as a local-preview card. */
function BoardGrid({
  gateway,
  boards,
  ...rowProps
}: BoardListProps & { readonly gateway: WorkspaceGateway }) {
  if (boards.length === 0) {
    return <p className="navigator__empty">No saved boards yet.</p>;
  }
  return (
    <ul className="board-grid" aria-label="Board thumbnail grid">
      {boards.map((board) => {
        const key = formatStoredPath(board.path);
        const current =
          rowProps.activePath !== null && key === formatStoredPath(rowProps.activePath);
        return (
          <li key={key}>
            <button
              className="board-grid__open"
              type="button"
              draggable
              aria-current={current ? "page" : undefined}
              onClick={(event) => {
                if (event.detail < 2) {
                  rowProps.onOpenBoard(board);
                }
              }}
              onDoubleClick={() => rowProps.onRenameBoard(board)}
              onDragEnd={rowProps.onDragEnd}
              onDragStart={(event) => rowProps.onDragStart(event, board)}
              title="Double-click to rename"
            >
              <BoardPreview board={board} gateway={gateway} />
              <strong>{board.name}</strong>
              <small>{board.path.slice(0, -1).join(" / ") || "Workspace"}</small>
            </button>
            <div className="board-grid__actions">
              <NavigatorIconButton
                label={`${rowProps.pinned.has(key) ? "Unpin" : "Pin"} ${board.name}`}
                pressed={rowProps.pinned.has(key)}
                onClick={() => rowProps.onTogglePin(board)}
              >
                <Star aria-hidden="true" />
              </NavigatorIconButton>
              <NavigatorIconButton
                label={`Manage ${board.name}`}
                onClick={() => rowProps.onManageBoard(board)}
              >
                <MoreHorizontal aria-hidden="true" />
              </NavigatorIconButton>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** Renders one consistently sized icon action with an accessible label. */
function NavigatorIconButton({
  children,
  label,
  onClick,
  pressed,
}: {
  readonly children: React.ReactNode;
  readonly label: string;
  readonly onClick: () => void;
  readonly pressed?: boolean;
}) {
  return (
    <IconButton
      className="navigator-icon-button"
      label={label}
      tooltipPlacement="right"
      aria-pressed={pressed}
      onClick={onClick}
    >
      {children}
    </IconButton>
  );
}
